import { RefElement } from 'widgets/toolbox/RefElement';

/**
 * @description Base InputPassword extending
 * @param InputPassword Base widget for extending
 * @returns InputPasswordExt class
 */
function InputPasswordExtClassCreator(InputPassword: import('widgets/forms/InputPassword').TInputPassword) {
    /**
     * @category widgets
     * @subcategory forms
     * @class InputPasswordExt
     * @augments BasicInput
     * @classdesc InputPasswordExt Widget extends InputPassword widget added functionality for adding class `m-focus` to label when input is focused
     */
    class InputPasswordExt extends InputPassword {
        prefs() {
            return {
                classesSuccess: 'm-success',
                classesStrength: 'm-strength',
                ...super.prefs()
            };
        }

        init() {
            super.init();
        }

        /**
         * @description Handles `focus` on password input
         * @listens dom#focus
         * @param el event source element
         */
        handleFocus(el: RefElement) {
            this.ref('label').addClass('m-focus');

            super.handleFocus(el);
        }

        /**
         * @description Handle click on button Show/Hide mask. Toggle type based on input type.
         * @listens dom#click
         */
        toggleMask() {
            const input = this.ref('field');
            const inputType = input.attr('type');
            const showButton = this.ref('showButton');
            const showIcon = this.ref('showIcon');
            const hideIcon = this.ref('hideIcon');

            if (inputType === 'password') {
                input.attr('type', 'text');
                showButton.attr('aria-pressed', 'true');
                showIcon.hide();
                hideIcon.show();
            } else {
                input.attr('type', 'password');
                showButton.attr('aria-pressed', 'false');
                showIcon.show();
                hideIcon.hide();
            }
        }

        /**
         * @description Listener for `input` event.
         * Usually used to display Show/Hide mask button when entered needed amount of symbols into password field.
         * @listens dom#input
         * @param el event source element
         */
        onPasswordInput(el: RefElement) {
            const entered = el && el.val();

            if (entered && entered.length) {
                this.showMaskButton();
                this.showNotification();
            }

            if (this.prefs().passwordStrengthEnabled) {
                this.showPasswordEstimator(el);
            }

            super.validate();
        }

        setError(error?: string) {
            this.hideCustomError();

            super.setError(error);
        }

        showNotification() {
            if (this.prefs().passwordConfirmation) {
                return;
            }

            const msg = this.prefs().validationConfig?.errors?.successMessage;

            this.hideCustomError();

            if (msg) {
                this.ref('field').addClass(this.prefs().classesStrength);
                this.ref('errorFeedback').addClass(this.prefs().classesStrength).setText(msg).show();
            }
        }

        hideCustomError() {
            this.ref('field').removeClass(`${this.prefs().classesStrength} ${this.prefs().classesSuccess}`);
            this.ref('errorFeedback').removeClass(`${this.prefs().classesStrength} ${this.prefs().classesSuccess}`);
        }
    }

    return InputPasswordExt;
}

export type TInputPasswordExt = ReturnType<typeof InputPasswordExtClassCreator>;

export type TInputPasswordExtInstance = InstanceType<TInputPasswordExt>;

export default InputPasswordExtClassCreator;
